import sortBy from 'lodash/sortBy';
import { detectSpecial } from '@next-order/next-order-engine';
import { getCurrentDayFlag, getDayFlag } from '../timeHelper';

/**
 * BOGO voucher (type "4") — self-contained module.
 *
 * Mirrors the POS implementation (src/orderEngine/getBogoVoucherDiscount.js
 * in the POS repo). All BOGO logic lives here; the only integration points
 * are a single `type === '4'` branch in engineGetOrderCalculations and the
 * time-slot check in validateVoucher. Types 1/2/3 are untouched.
 *
 * Deliberate v1 decisions (must stay in parity with POS):
 * 1. Half/half and four-quarter rows never participate in BOGO.
 * 2. `maxItemPrice` is a qualification cap, not a discount cap.
 * 3. Buy/get frames are all-or-nothing.
 * 4. The get item must be a separate cart row from the buy item.
 * 5. A computed discount of 0 means the voucher is treated as not applied.
 */

/* ----------------------------- config helpers ---------------------------- */

// Every config array stores ALL options with `isActive` booleans — an
// inactive entry is an unselected option, not a filter.
const getActiveIds = (list) =>
  (list || [])
    .filter((entry) => entry && entry.isActive === true)
    .map((entry) => entry._id);

const normalizeConfig = (config, isGetConfig) => {
  const quantity = Number(config?.quantity);
  const maxItemPriceRaw = config?.maxItemPrice;
  const maxItemPrice =
    isGetConfig &&
    maxItemPriceRaw !== null &&
    maxItemPriceRaw !== undefined &&
    maxItemPriceRaw !== '' &&
    Number(maxItemPriceRaw) > 0
      ? Number(maxItemPriceRaw)
      : null;

  return {
    quantity: quantity > 0 ? Math.floor(quantity) : 1,
    anyItem: isGetConfig && config?.anyItem === true,
    maxItemPrice,
    categoryIds: getActiveIds(config?.categories),
    menuItemIds: getActiveIds(config?.menuItems),
    sizeIds: getActiveIds(config?.menuSizes),
    subModifierIds: getActiveIds(config?.subModifiers),
  };
};

/* ------------------------------ row matching ------------------------------ */

const rowMatchesConfig = (row, config) => {
  // 1. Whole items only — half/half and quarter rows carry mis-priced
  //    single-half values, so matching them would compute wrong discounts.
  if (row.isHalf || row.isQuarter || row.isSecondHalf) return false;

  // 2. maxItemPrice (getConfig only) is a qualification cap: pricier rows
  //    are disqualified entirely, never partially discounted.
  if (config.maxItemPrice !== null && Number(row.price) > config.maxItemPrice)
    return false;

  // 3. anyItem (getConfig only): match — skip category/size/item checks.
  if (config.anyItem) return true;

  // 4. Category must be among the active categories.
  if (
    config.categoryIds.length > 0 &&
    !config.categoryIds.includes(row.categoryId)
  )
    return false;

  // 5. Size filter: menuSizes and subModifiers are parallel filters (same
  //    convention as type 3). If both lists are empty, no size filtering.
  const hasSizeFilter =
    config.sizeIds.length > 0 || config.subModifierIds.length > 0;
  if (
    hasSizeFilter &&
    !config.sizeIds.includes(row.sizeKey) &&
    !config.subModifierIds.includes(row.sizeKey)
  )
    return false;

  // 6. If active menuItems is non-empty, the row's item must be in it.
  if (config.menuItemIds.length > 0 && !config.menuItemIds.includes(row._id))
    return false;

  return true;
};

/* ------------------------------ benefit math ------------------------------ */

// Discount per matched get row, computed on the size price only (extra
// ingredients / modifier surcharges excluded — same convention as type 3).
const getBenefitForRow = (row, benefit) => {
  const unitPrice = Number(row.price) || 0;
  const benefitType = benefit?.benefitType || '3';

  if (benefitType === '1') {
    // Percentage: min(unitPrice × value / 100, maxDiscountAmount if > 0, unitPrice)
    const percentage = Number(benefit?.discountValue) || 0;
    let discount = (unitPrice * percentage) / 100;
    const cap = Number(benefit?.maxDiscountAmount);
    if (cap > 0) discount = Math.min(discount, cap);
    return Math.min(Math.max(discount, 0), unitPrice);
  }

  if (benefitType === '2') {
    // Fixed: min(value, unitPrice) — never negative.
    const value = Number(benefit?.discountValue) || 0;
    return Math.min(Math.max(value, 0), unitPrice);
  }

  // '3' free — full unit (size) price.
  return unitPrice;
};

/* -------------------------- candidate ordering ---------------------------- */

// Pricing strategy = candidate ordering. No post-hoc reassignment needed.
//  "1": buy fills price-descending, get fills price-ascending
//       (customer pays the priciest, cheapest is rewarded)
//  "2": buy ascending, get descending (priciest item is rewarded)
//  "3": natural order — the order the items were added to the cart.
//       The rows arrive price-sorted from the engine, so cart order must be
//       restored via primaryKey (assigned in cart explosion order).
//       Without this, a "buy 2 get 1" over overlapping pools can consume
//       the intended get item in the buy pass and discount the wrong row.
const orderCandidates = (rows, role, pricingStrategy) => {
  if (pricingStrategy === '3') return sortBy(rows, 'primaryKey');
  const ascending = sortBy(rows, (r) => Number(r.price));
  if (pricingStrategy === '2')
    return role === 'buy' ? ascending : [...ascending].reverse();
  // default "1"
  return role === 'buy' ? [...ascending].reverse() : ascending;
};

/* --------------------------- frame construction --------------------------- */

// One application of the deal: fill the buy frame, then the get frame from
// the remaining rows (or get-first when buyFirst is false). Each frame is
// all-or-nothing; returns null when either frame can't be filled completely.
const fillFrames = (availableRows, buyConfig, getConfig, pricingStrategy, buyFirst) => {
  const takeRows = (config, role, excludedKeys) => {
    const candidates = orderCandidates(
      availableRows.filter(
        (row) =>
          !excludedKeys.has(row.primaryKey) && rowMatchesConfig(row, config)
      ),
      role,
      pricingStrategy
    );
    if (candidates.length < config.quantity) return null;
    return candidates.slice(0, config.quantity);
  };

  const nothing = new Set();
  if (buyFirst) {
    const buyRows = takeRows(buyConfig, 'buy', nothing);
    if (!buyRows) return null;
    const getRows = takeRows(
      getConfig,
      'get',
      new Set(buyRows.map((row) => row.primaryKey))
    );
    if (!getRows) return null;
    return { buyRows, getRows };
  }

  const getRows = takeRows(getConfig, 'get', nothing);
  if (!getRows) return null;
  const buyRows = takeRows(
    buyConfig,
    'buy',
    new Set(getRows.map((row) => row.primaryKey))
  );
  if (!buyRows) return null;
  return { buyRows, getRows };
};

/* ------------------------------- detection -------------------------------- */

export const detectBogoDiscount = (voucher, rowItems) => {
  const result = { voucherDiscount: 0, usedIndexes: [] };

  const buyConfig = normalizeConfig(voucher?.buyConfig, false);
  const getConfig = normalizeConfig(voucher?.getConfig, true);
  const benefit = voucher?.benefit || {};
  const pricingStrategy = voucher?.pricingStrategy || '1';
  const maxApplicationsRaw = Number(voucher?.maxApplicationsPerOrder);
  const maxApplications =
    maxApplicationsRaw > 0 ? Math.floor(maxApplicationsRaw) : 1;

  let availableRows = (rowItems || []).filter((row) => !row.isSecondHalf);
  let totalDiscount = 0;

  for (let application = 0; application < maxApplications; application++) {
    // Buy-first is the primary pass. Fallback (important): if the greedy buy
    // pass consumed the only row the get pool could use (e.g. buy = any
    // pizza, get = Large only, cart has one Large), retry get-first —
    // without this, valid carts get rejected.
    const frames =
      fillFrames(availableRows, buyConfig, getConfig, pricingStrategy, true) ||
      fillFrames(availableRows, buyConfig, getConfig, pricingStrategy, false);
    if (!frames) break;

    totalDiscount += frames.getRows.reduce(
      (acc, row) => acc + getBenefitForRow(row, benefit),
      0
    );

    // Both buy and get rows are consumed — the paid item must never
    // double-dip into another application, a special, or another discount.
    const consumedKeys = new Set(
      [...frames.buyRows, ...frames.getRows].map((row) => row.primaryKey)
    );
    result.usedIndexes.push(...consumedKeys);
    availableRows = availableRows.filter(
      (row) => !consumedKeys.has(row.primaryKey)
    );
  }

  result.voucherDiscount = Number(totalDiscount.toFixed(2));
  return result;
};

/* -------------------------- availability gates ---------------------------- */

// Pickup orders require isAvailablePickup, delivery orders require
// isAvailableDelivery; walk-in ('0') and dine-in ('3') always pass. The
// day-of-week flag is checked against the delivery date if set, otherwise
// the store's current day (same as getPersonalizedSpecialFrames).
const isBogoVoucherAvailableForOrder = (voucher, currentOrder, storeConfig) => {
  if (currentOrder.orderType === '1' && !voucher.isAvailablePickup)
    return false;
  if (currentOrder.orderType === '2' && !voucher.isAvailableDelivery)
    return false;

  const dayText = currentOrder.deliveryDate
    ? getDayFlag(currentOrder.deliveryDate, 'YYYY-MM-DD HH:mm').dayText
    : getCurrentDayFlag(storeConfig?.timeZone)?.dayText;

  return Boolean(
    dayText &&
      Object.prototype.hasOwnProperty.call(voucher, dayText) &&
      voucher[dayText] === true
  );
};

/* ------------------------------ main export ------------------------------- */

// Same result shape as the engine's getVoucherDiscountedAmount so the
// caller-side handling (including the isItemOccupiedWithSpecials specials
// recompute) works identically to type 3.
export function getBogoVoucherDiscountedAmount(
  selectedVoucher,
  currentOrder,
  rowItems,
  specialDiscount,
  allSpecials,
  orderSetup,
  publicHolidays,
  storeConfig = {}
) {
  const resObj = {
    discount: 0,
    isVoucherValid: false,
    isItemOccupiedWithSpecials: false,
  };

  if (!currentOrder || selectedVoucher?.type !== '4') return resObj;
  if (!isBogoVoucherAvailableForOrder(selectedVoucher, currentOrder, storeConfig))
    return resObj;

  // Pass over all rows (including rows already consumed by specials).
  const discountWithoutFlag = detectBogoDiscount(selectedVoucher, rowItems);

  if (!selectedVoucher.isAvailableWithSpecials) {
    // Pass excluding rows already consumed by specials.
    const discountFlag = detectBogoDiscount(
      selectedVoucher,
      (rowItems || []).filter((row) => !row.itemsUsedInSpecials)
    );

    if (
      discountWithoutFlag.voucherDiscount !== 0 &&
      discountFlag.voucherDiscount === 0
    ) {
      // The voucher only works using rows occupied by specials. Recompute
      // what the specials would be worth WITHOUT those rows, then keep
      // whichever deal (voucher vs. specials) gives the customer more.
      const remainingRows = sortBy(
        (rowItems || []).filter(
          (row) => !discountWithoutFlag.usedIndexes.includes(row.primaryKey)
        ),
        'price'
      ).reverse();
      const specialObj = detectSpecial(
        currentOrder,
        remainingRows,
        allSpecials,
        orderSetup,
        publicHolidays,
        storeConfig
      );
      const specials = specialObj.specials || [];
      let specialsDiscountWithoutVoucherRows = 0;
      specials.forEach((s) => {
        specialsDiscountWithoutVoucherRows += Number(s.discount);
      });

      if (
        Number(specialDiscount) - specialsDiscountWithoutVoucherRows >
        discountWithoutFlag.voucherDiscount
      ) {
        // Keeping the specials is worth more — voucher does not apply.
        resObj.isItemOccupiedWithSpecials = false;
        resObj.isVoucherValid = false;
        resObj.discount = discountFlag.voucherDiscount;
      } else {
        resObj.isItemOccupiedWithSpecials = true;
        resObj.isVoucherValid = true;
        resObj.specials = specials;
        resObj.discount = discountWithoutFlag.voucherDiscount;
      }
    } else {
      resObj.discount = discountFlag.voucherDiscount;
      resObj.isVoucherValid = true;
    }
  } else {
    resObj.isVoucherValid = true;
    resObj.discount = discountWithoutFlag.voucherDiscount;
  }

  // A zero discount means the deal could not be applied to this cart —
  // treat the voucher as not applied (existing generic behavior).
  if (!(Number(resObj.discount) > 0)) {
    resObj.isVoucherValid = false;
    resObj.discount = 0;
  }

  return resObj;
}
